/**
 * DeleteInvestigationDialog
 *
 * GitHub-style destructive-action confirmation. The user must type exactly
 * "DELETE" (case-sensitive, strict equality — no trim, no whitespace
 * tolerance) before the Confirm Delete button becomes enabled.
 *
 * Design notes (ui-ux-pro-max applied):
 *   - Destructive emphasis: error.main color tokens, `color="error"` on
 *     the confirm button, warning block with role="alert".
 *   - 8dp spacing rhythm: p: 3 = 24px, gap: 2 = 16px, mt: 2 = 16px etc.
 *   - Touch targets >= 44px: minHeight: 44 on both action buttons.
 *   - Accessibility: aria-labelledby on dialog title, autofocus on input,
 *     aria-live="polite" for the success announcement region.
 *   - Transition: MUI Dialog's default scale+fade; honours prefers-reduced-
 *     motion via transitionDuration={0} when reduceMotion is true.
 *   - Enter submits when valid; Esc dismisses (Dialog handles Esc natively).
 *   - Inline error Alert stays inside the dialog body — no dismiss.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { investigate as investigateApi } from '@/helpers/ApiHelper';
import { ApiError } from '@/helpers/ApiHelper';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { formatRelative } from '@/utils/formatRelative';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DeleteInvestigationDialogProps {
  /** Controlled-open. The parent sets this true; the dialog calls onClose. */
  open: boolean;
  onClose: () => void;
  /** The investigation to be deleted. */
  investigation: {
    id: string;
    prompt: string;
    created_at: string;
  };
}

const CONFIRM_WORD = 'DELETE';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DeleteInvestigationDialog({
  open,
  onClose,
  investigation,
}: DeleteInvestigationDialogProps) {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  const [inputValue, setInputValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // aria-live region ref for screen reader announcement on success.
  const announcementRef = useRef<HTMLDivElement | null>(null);

  // Reset state when dialog opens/closes.
  useEffect(() => {
    if (!open) {
      setInputValue('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const isConfirmEnabled = inputValue === CONFIRM_WORD;

  const handleConfirm = useCallback(async () => {
    if (!isConfirmEnabled || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await investigateApi.delete(investigation.id);
      // Announce success to screen readers before navigating.
      if (announcementRef.current) {
        announcementRef.current.textContent = 'Investigation deleted';
      }
      // Fire the global toast event (handled by EasterEggs / Snackbar).
      window.dispatchEvent(
        new CustomEvent('sherlock:toast', {
          detail: { message: 'Investigation deleted' },
        }),
      );
      onClose();
      navigate('/investigations');
    } catch (err) {
      const detail =
        err instanceof ApiError ? err.detail : 'Delete failed. Please try again.';
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  }, [isConfirmEnabled, submitting, investigation.id, onClose, navigate]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && isConfirmEnabled && !submitting) {
        void handleConfirm();
      }
    },
    [handleConfirm, isConfirmEnabled, submitting],
  );

  // Truncate prompt for the summary row — max 80 chars.
  const promptPreview =
    investigation.prompt.length > 80
      ? investigation.prompt.slice(0, 79) + '…'
      : investigation.prompt;

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      transitionDuration={reduceMotion ? 0 : undefined}
      aria-labelledby="delete-investigation-title"
    >
      {/* Visually hidden aria-live region for success announcement */}
      <Box
        ref={announcementRef}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      />

      <DialogTitle
        id="delete-investigation-title"
        sx={{
          fontFamily: '"JetBrains Mono", monospace',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontSize: '0.875rem',
          fontWeight: 700,
          color: 'error.main',
          pb: 2,
        }}
      >
        Delete this investigation?
      </DialogTitle>

      <DialogContent sx={{ pt: 0, pb: 0 }}>
        <Stack spacing={2}>
          {/* Summary row — prompt preview + relative timestamp */}
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.8125rem',
                color: 'text.primary',
                wordBreak: 'break-word',
              }}
            >
              {promptPreview}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                color: (theme) =>
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- reason: text.faintest not in MUI default typing
                  (theme.palette as any).text?.faintest ?? theme.palette.text.disabled,
                display: 'block',
                mt: 0.5,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatRelative(investigation.created_at)}
            </Typography>
          </Box>

          {/* Red warning block */}
          <Box
            role="alert"
            sx={{
              display: 'flex',
              gap: 1.5,
              p: 2,
              borderRadius: 1,
              bgcolor: (theme) => `${theme.palette.error.main}14`,
              border: '1px solid',
              borderColor: 'error.main',
            }}
          >
            <WarningAmberIcon
              fontSize="small"
              sx={{ color: 'error.main', flexShrink: 0, mt: 0.125 }}
              aria-hidden="true"
            />
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.8125rem',
                color: 'error.main',
                lineHeight: 1.5,
              }}
            >
              This action is{' '}
              <Box component="strong" sx={{ fontWeight: 700 }}>
                permanent from the UI
              </Box>
              . Only an admin can recover deleted investigations from the database.
            </Typography>
          </Box>

          {/* Confirmation input */}
          <Box>
            <TextField
              label={`To confirm, type ${CONFIRM_WORD} (in capitals).`}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              fullWidth
              size="small"
              disabled={submitting}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              inputProps={{
                'aria-label': `Type ${CONFIRM_WORD} to confirm deletion`,
                // No maxLength — strict equality means extra chars auto-disable
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.875rem',
                },
                '& .MuiInputLabel-root': {
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.8125rem',
                },
              }}
            />
          </Box>

          {/* Inline error — stays open on failure */}
          {error !== null && (
            <Alert severity="error" role="alert" sx={{ mt: 0 }}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          variant="text"
          onClick={onClose}
          disabled={submitting}
          sx={{
            minHeight: 44,
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 12,
            textTransform: 'none',
            color: 'text.secondary',
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => void handleConfirm()}
          disabled={!isConfirmEnabled || submitting}
          startIcon={
            submitting ? (
              <CircularProgress size={16} color="inherit" aria-hidden="true" />
            ) : undefined
          }
          sx={{
            minHeight: 44,
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 12,
            textTransform: 'none',
          }}
        >
          {submitting ? 'Deleting…' : 'Confirm Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
